import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { useToast } from '../../context/ToastProvider';
import { useEffect, useRef, useState } from 'react';

import { auth } from 'apps/marsinet/src/firebase';
import { apiServices } from '../../services/apiServices';

type ActivationStatus = 'ready' | 'loading' | 'success' | 'error';

export const VerifyAccount: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const { showToast: show } = useToast();
  const hasVerified = useRef(false); // guard contra doble ejecución
  const [activationStatus, setActivationStatus] =
    useState<ActivationStatus>('loading');

  const handleVerifyAuto = async (token: string) => {
    try {
      await apiServices.authPublicApi.authPublicControllerVerifyEmail({
        data: { token },
      });
      // Espera extra antes de mostrar el éxito
      await new Promise((resolve) => setTimeout(resolve, 2000)); // 2 segundos extra
      setActivationStatus('success');
      show('success', 'Success', t('pages.verifyAccount.verified'), 6000);
    } catch (err) {
      console.error('[VerifyAccount] Error en verificación:', err);
      // Espera extra antes de mostrar el error
      await new Promise((resolve) => setTimeout(resolve, 3000)); // 2 segundos extra
      setActivationStatus('error');
      show('error', 'Error', t('pages.verifyAccount.error'), 6000);
    }
  };

  useEffect(() => {
    if (hasVerified.current) return;
    hasVerified.current = true;

    const runVerificationFlow = async () => {
      const params = new URLSearchParams(window.location.search);
      const langFromUrl =
        params.get('lang') || params.get('lng') || params.get('ln');
      const langFromStorage = localStorage.getItem('ln');
      const browserLang = navigator.language?.split('-')[0];
      const resolvedLang = (
        langFromUrl ||
        langFromStorage ||
        browserLang ||
        'es'
      ).toLowerCase();

      // Única fuente de verdad: los idiomas que i18next tiene realmente cargados
      // (config de i18n), así no hay que mantener otra lista aparte aquí.
      const supportedLngs = (i18n.options.supportedLngs || []).filter(
        (l) => l !== 'cimode'
      );
      const shortLang = resolvedLang.split('-')[0];
      const normalizedLang = supportedLngs.includes(shortLang)
        ? shortLang
        : 'es';

      localStorage.setItem('ln', normalizedLang);
      await i18n.changeLanguage(normalizedLang);

      const token = params.get('token');
      if (!token) {
        console.warn('[VerifyAccount] Falta el parámetro token en la URL');
        setActivationStatus('error');
        return;
      }

      await handleVerifyAuto(token);
    };

    void runVerificationFlow();
  }, [i18n]);

  return (
    <div className="col-12">
      <div
        className="flex align-items-center justify-content-center py-8 min-h-screen"
        style={{
          background: 'linear-gradient(60deg, #13dfb6 0%,#004e78 100%)',
        }}
      >
        <div
          className="flex flex-column align-items-center px-4 py-5 surface-100 shadow-2 border-round-2xl"
          style={{ maxWidth: '400px', width: '100%' }}
        >
          <div className="text-center mb-4">
            <img
              src="/assets/marsi-azul-degradado-fondo-blanco.webp"
              style={{ width: '293px' }}
              alt="Logotipo de Marsinet"
            />
          </div>

          <div className="mt-6 mb-5 font-bold text-3xl text-900 text-center">
            {activationStatus === 'success'
              ? t('pages.verifyAccount.activatedTitle')
              : activationStatus === 'error'
                ? t('pages.verifyAccount.errorTitle')
                : t('pages.verifyAccount.pendingTitle')}
          </div>

          <p className="text-700 text-xl mt-0 mb-6 text-center">
            {activationStatus === 'success'
              ? t('pages.verifyAccount.verified')
              : activationStatus === 'error'
                ? t('pages.verifyAccount.error')
                : t('pages.verifyAccount.pendingDescription')}
          </p>

          {activationStatus === 'loading' && (
            <div className="text-center w-full">
              <i
                className="pi pi-spin pi-spinner"
                style={{ fontSize: '2rem' }}
              />
              <p className="mt-4 text-600">
                {t('pages.verifyAccount.pendingDescription')}
              </p>
            </div>
          )}

          {activationStatus === 'success' && (
            <div className="text-center w-full">
              <div className="text-green-600 text-center mb-4">
                {t('pages.verifyAccount.alreadyActivated') ||
                  'Tu email ha sido verificado correctamente.'}
              </div>
            </div>
          )}
          <Button
            type="button"
            className="w-full p-button-secondary"
            label={t('pages.verifyAccount.backToLogin')}
            onClick={() => navigate('/login')}
          />
          {activationStatus === 'error' && (
            <div className="text-center w-full">
              <Button
                type="button"
                className="w-full p-button-secondary"
                label={t('pages.verifyAccount.backToLogin')}
                onClick={() => navigate('/login')}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyAccount;
